import { useCallback, useEffect, useMemo, useState } from "react";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { toDateTime } from "Helpers/Format";

import UseNotification from "Hooks/UseNotification";
import { UsePaginatedFetch } from "Hooks/UsePaginatedFetch";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import Loading from "Components/Admin/Loading";
import Table from "Components/Admin/Table";
import { TableRowLink } from "Components/Admin/TableRowLink";

import LinkStatusSelect, { LinkStatusContext } from "../LinkStatusSelect";
import * as S from "./styles";

const projectDetailUrl = (projectId) =>
  projectId ? AdminRoutes.UpdateProjects.path.replace(":id", projectId) : null;

// Aba "Pré-seleção de Projetos", em DUAS listas desde 18/08/2026 (pedido do
// cliente: "o volume de projetos é grande").
//
// - Acima, os projetos JÁ VINCULADOS a esta seletiva — é onde o admin edita o
//   status de vínculo abaixo de "Apresentado", que a aba "Projetos Apresentados"
//   não mostra.
// - Abaixo, os CANDIDATOS: ranqueados por similaridade com as características da
//   seletiva e associáveis em lote.
//
// As duas são disjuntas (o backend filtra por `linked_only`): antes o vinculado
// era forçado para dentro da lista única, justamente por falta de lugar próprio.
const PreSelection = ({ id, onLinked, statusOptions = [] }) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();

  const [selected, setSelected] = useState([]);
  const [linking, setLinking] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // As listas vêm do UsePaginatedFetch (imutáveis aqui), então guardamos as
  // alterações de status feitas na própria tabela para refletir na tela sem
  // recarregar tudo. Chave: id do vínculo (project_sponsors.id).
  const [statusOverrides, setStatusOverrides] = useState({});

  const onLinkStatusSaved = useCallback((sponsorId, newStatusId, label) => {
    setStatusOverrides((current) => ({
      ...current,
      [sponsorId]: { status_id: newStatusId, link_status: label },
    }));
  }, []);

  const linkStatusCtx = useMemo(
    () => ({ statusOptions, onSaved: onLinkStatusSaved }),
    [statusOptions, onLinkStatusSaved],
  );

  // Uma busca por lista. ⚠️ Cada uma tem a PRÓPRIA contagem (o backend conta o
  // array já filtrado): reaproveitar a contagem da outra faria o
  // UsePaginatedFetch pedir páginas que não existem.
  const linkedFetch = UsePaginatedFetch({
    method: "post",
    path: "/select-projects/preselect-projects",
    body: { id, linked_only: true },
    responseAccessor: "projects",
    pageSize: 500,
  });

  const candidatesFetch = UsePaginatedFetch({
    method: "post",
    path: "/select-projects/preselect-projects",
    body: { id, linked_only: false },
    responseAccessor: "projects",
    pageSize: 500,
  });

  useEffect(() => {
    if (!id) return;

    linkedFetch.setStartFetch(true);
    candidatesFetch.setStartFetch(true);
    // eslint-disable-next-line
  }, [id]);

  const projectColumn = {
    Header: "Projeto",
    accessor: "project_name",
    Cell: ({ cell }) => (
      <TableRowLink
        to={projectDetailUrl(cell.row.original.project_id)}
        label={cell.row.original.project_name}
        fallbackId={cell.row.original.project_id}
        newTab
      />
    ),
  };

  const similarityColumn = {
    // Nota de 0 a 100 calculada no backend contra as características da
    // seletiva (lei, território, área, ODS, data de realização e aporte
    // mínimo). O accessor devolve o NÚMERO — é dele que a ordenação da tabela
    // depende; o "%" é só apresentação.
    Header: "Similaridade",
    accessor: (row) => row.similarity ?? 0,
    Cell: ({ cell }) => `${cell.row.original.similarity ?? 0}%`,
  };

  const updatedAtColumn = {
    // Desempate da ordenação por similaridade: entre notas iguais, o projeto
    // alterado mais recentemente vem primeiro.
    Header: "Última modificação",
    accessor: (row) => (row.updated_at ? toDateTime(row.updated_at) : ""),
  };

  const linkStatusColumn = {
    Header: "Status da Associação",
    accessor: "link_status",
    Cell: ({ cell }) => {
      const row = cell.row.original;
      const override = statusOverrides[row.project_sponsor_id];
      const label = override?.link_status ?? row.link_status;

      // Sem vínculo identificado não há o que editar: mostra o rótulo que
      // houver ("Pré selecionado" quando nem status existe).
      if (!row.project_sponsor_id) return label || "Pré selecionado";

      return (
        <LinkStatusSelect
          sponsorId={row.project_sponsor_id}
          statusId={override?.status_id ?? row.status_id}
          fallbackLabel={label}
          validationEmailSentAt={row.validation_email_sent_at}
        />
      );
    },
  };

  const linkedColumns = [
    projectColumn,
    { Header: "Proponente", accessor: "proponent" },
    { Header: "Status do Projeto", accessor: "project_status" },
    linkStatusColumn,
    updatedAtColumn,
  ];

  const candidateColumns = [
    projectColumn,
    similarityColumn,
    { Header: "Proponente", accessor: "proponent" },
    { Header: "Status do Projeto", accessor: "project_status" },
    linkStatusColumn,
    updatedAtColumn,
  ];

  const onSelectChange = (rows) => {
    setSelected((rows || []).filter(Boolean));
  };

  // Já vinculados são ignorados no envio (o backend também deduplica). Com as
  // listas disjuntas isso virou salvaguarda, não caminho normal.
  const toLink = selected.filter((row) => row && !row.is_linked);

  const associate = () => {
    const project_ids = toLink.map((row) => row.project_id);

    if (project_ids.length === 0) return;

    setLinking(true);

    api
      .post("/select-projects/link-projects", { id, project_ids })
      .then(({ data }) => {
        setLinking(false);
        showNotification(
          "success",
          "Sucesso!",
          data?.message || "Projetos associados à seletiva.",
        );
        setSelected([]);
        setStatusOverrides({});
        setReloadKey((key) => key + 1);
        // As DUAS listas são refeitas: o projeto associado migra de baixo para
        // cima sem recarregar a página.
        linkedFetch.setStartFetch(true);
        candidatesFetch.setStartFetch(true);
        if (onLinked) onLinked();
      })
      .catch((error) => {
        setLinking(false);
        return HTTPErrorCallback(error);
      });
  };

  const isLoading = (fetch) =>
    fetch.requestInProgress && !fetch.firstResponseReceived;

  return (
    <S.Wrapper>
      <LinkStatusContext.Provider value={linkStatusCtx}>
        <S.Block>
          <S.Header>
            <S.Info>
              Projetos já selecionados para esta seletiva. Edite o status da
              associação diretamente na lista.
            </S.Info>
          </S.Header>

          {isLoading(linkedFetch) ? (
            <Loading />
          ) : linkedFetch.data.length > 0 ? (
            <Table
              key={`linked-${reloadKey}`}
              name="Projetos já selecionados"
              data={linkedFetch.data}
              columns={linkedColumns}
              download={false}
            />
          ) : (
            <S.Empty>Nenhum projeto vinculado a esta seletiva ainda.</S.Empty>
          )}
        </S.Block>

        <S.Block>
          <S.Header>
            <S.Info>
              Projetos ordenados por similaridade com as características da
              seletiva. Selecione os desejados e clique em associar.
            </S.Info>
            <Button
              type="button"
              text={
                linking
                  ? "Associando..."
                  : `Associar selecionados${
                      toLink.length ? ` (${toLink.length})` : ""
                    }`
              }
              onClick={associate}
              disabled={linking || toLink.length === 0}
            />
          </S.Header>

          {isLoading(candidatesFetch) ? (
            <Loading />
          ) : candidatesFetch.data.length > 0 ? (
            <Table
              key={`preselection-${reloadKey}`}
              name="Pré-seleção de Projetos"
              data={candidatesFetch.data}
              columns={candidateColumns}
              selectable={true}
              onSelectChange={onSelectChange}
              download={false}
            />
          ) : (
            <S.Empty>
              Nenhum projeto com similaridade suficiente para esta seletiva.
            </S.Empty>
          )}
        </S.Block>
      </LinkStatusContext.Provider>
    </S.Wrapper>
  );
};

export default PreSelection;
